import { Link } from 'react-router-dom';
import React, { useState } from 'react';
import { App, Button, Card, Input, Result, Space, Statistic, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined } from '@ant-design/icons';
import { formatDateTime } from '../../utils/format';
import { usePaySegments, useRecalculatePaySegment, useServerTime, useSetServerTime } from './queries';
import { PaySegment } from './types';

const { Text } = Typography;
const show = (value: React.ReactNode) => value ?? '—';

export const PaySegmentsPage: React.FC = () => {
  const { message } = App.useApp();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [userInput, setUserInput] = useState('');
  const [userID, setUserID] = useState<string>();
  const [timeInput, setTimeInput] = useState('');
  const [purchaseUserID, setPurchaseUserID] = useState('');
  const [productID, setProductID] = useState('');
  const [purchaseAt, setPurchaseAt] = useState('');
  const [asOf, setAsOf] = useState('');
  const [requestID, setRequestID] = useState<string>();
  const [lastRecalculation, setLastRecalculation] = useState<string>();
  const list = usePaySegments(page, perPage, userID);
  const serverTime = useServerTime();
  const setTime = useSetServerTime();
  const recalculate = useRecalculatePaySegment();

  const runHistoricalRecalculation = () => {
    const idempotencyKey = requestID ?? crypto.randomUUID();
    setRequestID(idempotencyKey);
    recalculate.mutate({
      user_id: Number(purchaseUserID),
      product_id: Number(productID),
      request_id: idempotencyKey,
      purchase_at: new Date(`${purchaseAt}Z`).toISOString(),
      as_of: new Date(`${asOf}Z`).toISOString(),
    }, {
      onSuccess: (result) => {
        setLastRecalculation(`User #${result.user_id}: segment ${result.segment_id ?? '—'}, R ${result.recency_days}d / F ${result.frequency_30d} / M ${result.monetary_30d_cents}; snapshot ${result.snapshot_date}`);
        setRequestID(undefined);
        message.success('Mock purchase inserted and historical segment recalculated');
      },
      onError: (error) => message.error(error instanceof Error ? error.message : 'Failed to recalculate pay segment'),
    });
  };

  const updateTime = (value: string | null) => setTime.mutate(value, {
    onSuccess: (result) => {
      setTimeInput('');
      message.success(result.overridden ? 'Server time changed' : 'Server time reset');
    },
    onError: (error) => message.error(error instanceof Error ? error.message : 'Failed to change server time'),
  });

  const columns: ColumnsType<PaySegment> = [
    { title: 'User', key: 'user', fixed: 'left', width: 170, render: (_, row) => <><div><Link to={`/users/${row.user_id}`}>{row.username}</Link></div><Link to={`/users/${row.user_id}`}>#{row.user_id}</Link></> },
    { title: 'Payer', dataIndex: 'is_payer', width: 80, render: (value: boolean | null) => value == null ? '—' : <Tag color={value ? 'green' : 'default'}>{value ? 'Yes' : 'No'}</Tag> },
    { title: 'RFM values', key: 'rfm', width: 190, render: (_, row) => row.calculated_at ? `R ${show(row.recency_days)}d · F ${show(row.frequency_30d)} · M ${show(row.monetary_30d_cents)}` : 'Not calculated' },
    { title: 'RFM scores', key: 'scores', width: 135, render: (_, row) => row.rfm_code == null ? '—' : `${row.r_score}-${row.f_score}-${row.m_score} (${row.rfm_code})` },
    { title: 'Segment', key: 'segment', width: 190, render: (_, row) => row.segment_name ? <><div>{row.segment_name}</div><Text type="secondary">Tier {row.quality_priority} · {row.segment_code}</Text></> : '—' },
    { title: 'Transition', key: 'transition', width: 130, render: (_, row) => row.transition ? <><Tag color={row.transition === 'upgrade' ? 'green' : row.transition === 'downgrade' ? 'red' : 'blue'}>{row.transition}</Tag><div><Text type="secondary">Group {row.group_id}</Text></div></> : '—' },
    { title: 'Treatment', key: 'treatment', width: 175, render: (_, row) => row.transition_duration_days == null ? '—' : <><div>{row.transition_duration_days} days</div><Text type="secondary">Until {formatDateTime(row.transition_expires_at)}</Text></> },
    { title: 'Last calculated', dataIndex: 'calculated_at', width: 180, render: formatDateTime },
  ];

  return (
    <Space direction="vertical" size="large" className="w-full">
      <Card size="small" title="Backend server time (UTC)">
        {serverTime.error ? <Result status="error" title="Failed to load server time" extra={<Button onClick={() => serverTime.refetch()}>Retry</Button>} /> : (
          <Space wrap size="middle">
            <Statistic title={serverTime.data?.overridden ? 'Overridden time' : 'Current time'} value={formatDateTime(serverTime.data?.time)} />
            <Tag color={serverTime.data?.overridden ? 'orange' : 'green'}>{serverTime.data?.overridden ? 'OVERRIDDEN' : 'REAL TIME'}</Tag>
            <Input aria-label="Server time" type="datetime-local" value={timeInput} onChange={(event) => setTimeInput(event.target.value)} style={{ width: 230 }} />
            <Button type="primary" disabled={!timeInput} loading={setTime.isPending} onClick={() => updateTime(new Date(`${timeInput}Z`).toISOString())}>Change time</Button>
            <Button disabled={!serverTime.data?.overridden} loading={setTime.isPending} onClick={() => updateTime(null)}>Reset to now</Button>
            <Button icon={<ReloadOutlined />} loading={serverTime.isFetching} onClick={() => serverTime.refetch()}>Refresh</Button>
          </Space>
        )}
      </Card>

      <Card size="small" title="Mock purchase and historical recalculation (UTC)">
        <Space wrap size="middle">
          <Input aria-label="Purchase user ID" placeholder="User ID" inputMode="numeric" value={purchaseUserID} onChange={(event) => { setPurchaseUserID(event.target.value.replace(/\D/g, '')); setRequestID(undefined); }} style={{ width: 130 }} />
          <Input aria-label="IAP-linked shop product ID" placeholder="IAP-linked product ID" inputMode="numeric" value={productID} onChange={(event) => { setProductID(event.target.value.replace(/\D/g, '')); setRequestID(undefined); }} style={{ width: 180 }} />
          <Input aria-label="Mock purchase date" type="datetime-local" value={purchaseAt} onChange={(event) => { setPurchaseAt(event.target.value); setRequestID(undefined); }} style={{ width: 220 }} />
          <Input aria-label="Calculate as of date" type="datetime-local" value={asOf} onChange={(event) => { setAsOf(event.target.value); setRequestID(undefined); }} style={{ width: 220 }} />
          <Button type="primary" loading={recalculate.isPending} disabled={!purchaseUserID || !productID || !purchaseAt || !asOf} onClick={runHistoricalRecalculation}>Insert and recalculate</Button>
        </Space>
        {lastRecalculation && <div><Text>{lastRecalculation}</Text></div>}
      </Card>

      <Space wrap>
        <Input.Search aria-label="Filter by user ID" placeholder="Exact user ID" allowClear value={userInput}
          onChange={(event) => { setUserInput(event.target.value.replace(/\D/g, '')); if (!event.target.value) { setUserID(undefined); setPage(1); } }}
          onSearch={(value) => { setUserID(value || undefined); setPage(1); }} style={{ width: 260 }} />
        <Button icon={<ReloadOutlined />} loading={list.isFetching} onClick={() => list.refetch()}>Refresh</Button>
      </Space>

      {list.error ? <Result status="error" title="Failed to load pay segments" subTitle={list.error instanceof Error ? list.error.message : 'An unexpected error occurred'} extra={<Button onClick={() => list.refetch()}>Retry</Button>} /> : (
        <Table rowKey="user_id" columns={columns} dataSource={list.data?.data ?? []} loading={list.isLoading} scroll={{ x: 1250 }}
          pagination={{ current: page, pageSize: perPage, total: list.data?.meta.total ?? 0, showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100], disabled: list.isPlaceholderData, showTotal: (total) => `${total} users`,
            onChange: (nextPage, nextSize) => { setPage(nextSize === perPage ? nextPage : 1); setPerPage(nextSize); } }} />
      )}
    </Space>
  );
};
